/**
 * An empty cell cannot mean "clear this value": a CSV that simply does not carry a column, or carries
 * it only for some rows, would then wipe metadata the importer was never asked to touch. So clearing
 * is opt-in per cell, spelled out with this token.
 */
export const CLEAR_SENTINEL = 'NULL'

export type CellIntent = { kind: 'absent' } | { kind: 'clear' } | { kind: 'value'; value: string }

/**
 * Whether a raw cell value is the clear token, for callers that hold the value rather than the row —
 * column validators, which decide whether a cell is a legal entry at all.
 *
 * They have to agree with [readCell]: a validator that does not know the token rejects it as an
 * unmatched value, raises the column's warning and routes the user to the re-map helper, which has no
 * way to express "clear". The import then cannot use the very token the UI advertises.
 */
export const isClearToken = (raw: string | undefined): boolean =>
  raw?.trim().toLowerCase() === CLEAR_SENTINEL.toLowerCase()

/** Case-insensitive so `null`, `Null` and `NULL` all read as intent rather than as a value. */
export const readCell = (row: Record<string, string>, column: string | undefined): CellIntent => {
  const raw = column ? row[column]?.trim() : undefined
  if (!raw) {
    return { kind: 'absent' }
  }
  return isClearToken(raw) ? { kind: 'clear' } : { kind: 'value', value: raw }
}
